import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextareaAutosize from 'react-textarea-autosize';
import { Collapse } from '@/components/Collapse';
import { emailAsArray } from '@/features/permissions/lib/collaboratorForm.lib';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import { OrganizationRole } from '@/types/gql.generated';
import {
  useToast,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Textarea,
} from '@/ui';
import {
  getGQLErrorMessage,
  isGQLError,
  isGQLErrorOfType,
} from '@/utils/errors';
import {
  AddOrganizationMembersSchema,
  type AddOrganizationMembersDto,
} from '../dto/addMembers.dto';
import { useInviteOrganizationMembers } from '../hooks/useInviteOrganizationMembers';
import type { OrgMember } from '../hooks/useOrganizationMembers';
import { getSeatUsage } from '../lib/seatLimit';

type Props = {
  organizationId: string;
  members: OrgMember[];
  /**
   * Lowercased emails that already have a pending invite, expired included —
   * an expired invite is resent rather than re-created, so it still counts as
   * a duplicate even though it no longer holds a seat.
   */
  pendingInviteEmails: string[];
  /**
   * Seats occupied: accepted members + live (non-expired) pending invites — the
   * same count the server's invite gate uses.
   */
  occupiedSeats: number;
  seatLimit: number | null | undefined;
  onClose: () => void;
};

export const AddMembersSection = ({
  organizationId,
  members,
  pendingInviteEmails,
  occupiedSeats,
  seatLimit,
  onClose,
}: Props) => {
  const { t } = useTranslation('settingsModal', {
    keyPrefix: 'organizations',
  });
  const { inviteMembers, isPending } =
    useInviteOrganizationMembers(organizationId);
  const toast = useToast();
  const isMobileBreakpoint = useIsMobileBreakpoint();

  const {
    register,
    handleSubmit: onSubmit,
    setError: setFieldError,
    reset: resetForm,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: { email: '' },
    resolver: standardSchemaResolver(AddOrganizationMembersSchema),
  });

  const watchEmail = watch('email');
  const { remaining, hasReachedLimit, isUnlimited } = getSeatUsage(
    seatLimit,
    occupiedSeats
  );

  const handleSubmit: SubmitHandler<AddOrganizationMembersDto> = (values) => {
    // Ignore repeat submits while an invite request is already in flight.
    if (hasReachedLimit || isPending) {
      return;
    }

    const existingEmails = new Set([
      ...members
        .map((member) => member.user.email?.toLowerCase())
        .filter((email): email is string => !!email),
      ...pendingInviteEmails,
    ]);
    const seen = new Set<string>();
    const newEmails = emailAsArray(values.email).filter((email) => {
      const lower = email.toLowerCase();
      if (existingEmails.has(lower) || seen.has(lower)) {
        return false;
      }
      seen.add(lower);
      return true;
    });

    if (newEmails.length === 0) {
      setFieldError('email', { message: t('invite.all_existing') });
      return;
    }

    const cappedEmails = isUnlimited
      ? newEmails
      : newEmails.slice(0, remaining);
    const membersInput = cappedEmails.map((email) => ({
      email,
      role: OrganizationRole.Editor,
    }));

    inviteMembers(
      { members: membersInput },
      {
        onError: (err) => {
          setFieldError('email', {
            message: isGQLErrorOfType(err, 'OrganizationSeatLimitReached')
              ? t('invite.limit_reached')
              : isGQLError(err)
                ? (getGQLErrorMessage(err) ?? t('invite.error'))
                : t('invite.error'),
          });
        },
        onSuccess: () => {
          const skippedCount = newEmails.length - membersInput.length;
          toast.notify(
            membersInput.length === 1
              ? t('invite.success', {
                  count: 1,
                  email: membersInput[0].email,
                })
              : t('invite.success', { count: membersInput.length })
          );
          resetForm({ email: '' });
          // Some entered emails may have been dropped to fit remaining seats —
          // tell the user rather than silently truncating, and stay open so the
          // message has somewhere to live.
          if (skippedCount > 0) {
            setFieldError('email', {
              message: t('invite.skipped', { count: skippedCount }),
            });
            return;
          }
          onClose();
        },
      }
    );
  };

  // Remaining-seat messaging lives on the surrounding section, which is also
  // what decides whether this form is reachable at all.
  return (
    <Flex as="form" direction="column" onSubmit={onSubmit(handleSubmit)}>
      <FormControl isInvalid={!!errors.email}>
        <Textarea
          as={TextareaAutosize}
          autoFocus={!isMobileBreakpoint}
          placeholder={t('invite.placeholder')}
          rows={1}
          {...register('email')}
        />
        <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
      </FormControl>

      {/* Cancel stays put whether or not anything has been typed — it's the only
          way back out of the expanded form. */}
      <Flex align="center" gap="3" justify="flex-end" mt="2">
        <Button isDisabled={isPending} variant="link" onClick={onClose}>
          {t('invite.cancel')}
        </Button>
        <Collapse in={watchEmail.length > 0}>
          <Button isLoading={isPending} type="submit">
            {t('invite.submit')}
          </Button>
        </Collapse>
      </Flex>
    </Flex>
  );
};
